import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useCreateAdmin } from '../api'
import { ACCEPTED_FILE_TYPES, formSchema } from './schema'
import FormBuilder from '../../../../../components/app/formBuilder'
import { Button } from '../../../../../components/common'
import intershipImage from '../../../../../assets/images/internship-bg.png'
import { useAuthStore } from '../../../../../store/authStore'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

type ExtendedAdminSchema = {
  // Personal Details
  first_name: string
  last_name: string
  email: string
  address: string
  gender: string

  dob: string
  contact_number: string

  // Educational Background
  university: string
  degree_program: string
  major: string
  year_of_study: string
  year_of_study_other?: string
  cgpa: string

  // Internship Preferences
  duration?: string
  duration_other?: string
  interests: string[]
  interests_other?: string
  start_date: string

  // Experience and Skills
  skills: string
  resume: File | null

  // Motivation & Goals
  why_interested: string

  // Declarations
  information_accurate: boolean
  internship_program: boolean

  // Additional fields
  internship_id?: string
}

export default function InternshipCreate() {
  const methods = useForm<ExtendedAdminSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      address: '',
      gender: '',
      dob: '',
      contact_number: '',
      university: '',
      degree_program: '',
      major: '',
      year_of_study: '',
      year_of_study_other: '',
      cgpa: '',
      duration: '',
      duration_other: '',
      interests: [],

      interests_other: '',
      start_date: '',
      skills: '',
      resume: null,
      why_interested: '',
      information_accurate: false,
      internship_program: false,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })
  const navigate = useNavigate()
  const { userData } = useAuthStore()
  const { reset } = methods
  const location = useLocation()
  const internshipId = location.state?.prefilledData?.internshipId

  const { mutate, isLoading: isCreating } = useCreateAdmin(onSuccess)

  function onSuccess() {
    methods.reset()
    navigate('/home')
  }
  const control = methods.control

  const selectedGraduation = useWatch({
    control,
    name: 'year_of_study',
  })
  const selectedDuration = useWatch({
    control,
    name: 'duration',
  })
  const selectedInterests = useWatch({
    control,
    name: 'interests',
  })
  console.log('Selected Graduation:', selectedGraduation)
  const { handleSubmit } = methods

  const onSubmit = (details: ExtendedAdminSchema) => {
    const formData = new FormData()

    formData.append('internship_id', internshipId ?? '')
    formData.append('address', details.address)
    formData.append('gender', details.gender || '')
    formData.append(
      'dob',
      details.dob ? new Date(details.dob).toISOString().slice(0, 10) : ''
    )
    formData.append('contact_number', details.contact_number)

    // Educational Background
    formData.append('university', details.university)
    formData.append('degree_program', details.degree_program)
    formData.append('major', details.major)
    formData.append(
      'year_of_study',
      details.year_of_study === 'other'
        ? details.year_of_study_other || ''
        : details.year_of_study
    )
    formData.append('cgpa', details.cgpa)

    // Internship Preferences
    const duration =
      details.duration === 'other'
        ? details.duration_other || ''
        : details.duration
    formData.append('duration', duration ?? '')

    const interests = details.interests.includes('other')
      ? [
          ...details.interests.filter((item) => item !== 'other'),
          details.interests_other,
        ]
      : details.interests

    interests.forEach((interest, index) => {
      formData.append(`interests[${index}]`, interest ?? '')
    })

    formData.append(
      'start_date',
      details.start_date
        ? new Date(details.start_date).toISOString().slice(0, 10)
        : ''
    )

    // Experience and Skills
    formData.append('skills', details.skills)
    if (details.resume) {
      formData.append('resume', details.resume)
    }

    // Motivation & Goals
    formData.append('why_interested', details.why_interested)

    mutate(formData)
  }

  useEffect(() => {
    if (userData) {
      const [firstName = '', lastName = ''] = userData.name?.split(' ') || []
      reset({
        first_name: firstName,
        last_name: lastName,
        email: userData.email || '',
        contact_number: userData.mobile || '',
      })
    }
  }, [userData, reset])

  console.log(methods.formState.errors)

  const personalDetails = [
    {
      name: 'first_name',
      label: 'First Name',
      type: 'text',
      placeholder: 'Enter First Name',
      required: true,
      disabled: false,
    },
    {
      name: 'email',
      label: 'Email Address',
      type: 'email',
      placeholder: 'Enter Email Address',
      required: true,
      disabled: false,
    },
    {
      name: 'last_name',
      label: 'Last Name',
      type: 'text',
      placeholder: 'Last Name',
      required: true,
      disabled: false,
    },
    {
      name: 'address',
      label: 'Current Address',
      type: 'text',
      placeholder: 'Enter Address',
      required: true,
      disabled: false,
    },

    {
      name: 'gender_name' as const,
      label: 'Gender',
      type: 'custom_select' as const,
      placeholder: 'Select Gender',
      required: true,
      disabled: false,
      desc: 'name',
      descId: 'id',
      id: 'gender',
      data: [
        { name: 'Male', id: 'Male' },
        { name: 'Female', id: 'Female' },
        { name: 'Other', id: 'Other' },
        { name: 'Prefer not to say', id: 'Prefer not to say' },
      ],
    },
    {
      name: 'dob',
      label: 'Date of Birth',
      type: 'date',
      placeholder: '',
      required: true,
      disabled: false,
    },
    {
      name: 'contact_number',
      label: 'Contact Number',
      type: 'number',
      placeholder: 'Enter your Contact Number',
      required: true,
      disabled: false,
    },
  ]

  const educationDetails = [
    {
      name: 'university',
      label: 'University/College Name',
      type: 'text',
      placeholder: 'Enter your University/College',
      disabled: false,
    },
    {
      name: 'degree_program',
      label: 'Degree Program:',
      type: 'text',
      placeholder: 'Enter your Degree Program',
      disabled: false,
    },
    {
      name: 'major',
      label: 'Major/Specialization',
      type: 'text',
      placeholder: 'Select your Program',
      disabled: false,
    },

    {
      name: 'year_of_study' as const,
      label: 'Year of Study',
      type: 'custom_select' as const,
      placeholder: 'Enter your graduation year',
      required: true,
      disabled: false,
      desc: 'name',
      descId: 'id',
      id: 'year_of_study',
      data: [
        { name: '1st Year', id: '1st Year' },
        { name: '2nd Year', id: '2nd Year' },
        { name: '3rd Year', id: '3rd Year' },
        { name: 'Final Year', id: 'Final Year' },
        { name: 'Other', id: 'Other' },
      ],
    },
    ...(selectedGraduation === 'Other'
      ? [
          {
            name: 'graduation_other',
            label: 'Specify Year',
            type: 'text',
            placeholder: 'Enter your graduation year',
            disabled: false,
          },
        ]
      : []),

    {
      name: 'cgpa',
      label: 'CGPA/Percentage (latest semester)',
      type: 'text',
      placeholder: 'Enter your Academic Performance',
      disabled: false,
    },
  ]

  const internshipPreferance = [
    {
      name: 'duration' as const,
      label: 'Preferred Internship Duration',
      type: 'custom_select' as const,
      placeholder: 'Select your Preferred Internship Duration',
      required: true,
      disabled: false,
      desc: 'name',
      descId: 'id',
      id: 'duration',
      data: [
        { name: '4 weeks', id: '4 weeks' },
        { name: '8 weeks', id: '8 weeks' },
        { name: 'Other', id: 'Other' },
      ],
    },
    ...(selectedDuration === 'Other'
      ? [
          {
            name: 'duration_other',
            label: 'Specify Duration',
            type: 'text',
            placeholder: 'Enter your duration',
            disabled: false,
          },
        ]
      : []),

    {
      name: 'interests',
      label: 'Area(s) of Interest:',
      type: 'multi_select',
      placeholder: 'Select your Area of interest',
      isMultiple: true,
      disabled: false,
      data: [
        { name: 'Marketing', id: 'marketing' },
        { name: 'Finance', id: 'finance' },
        { name: 'Human Resources', id: 'hr' },
        { name: 'IT / Software Development', id: 'it' },
        { name: 'Research & Analysis', id: 'research' },
        { name: 'Design / Media', id: 'design' },
        { name: 'Other', id: 'other' },
      ],
    },
    ...(selectedInterests?.includes('Other')
      ? [
          {
            name: 'interests_other',
            label: 'Specify Area of interest',
            type: 'text',
            placeholder: 'Enter your Area of interest',
            disabled: false,
          },
        ]
      : []),

    {
      name: 'start_date',
      label: 'Preferred Start Date',
      type: 'date',
      placeholder: 'Select your Preferred Start Date',
      disabled: false,
    },
  ]

  const experienceSkills = [
    {
      name: 'skills',
      label:
        "Key Skills or Tools You're Proficient In (e.g., MS Office, Canva, Python, etc.)",
      type: 'text',
      placeholder: 'List your skills separated by commas',
      disabled: false,
    },

    {
      name: 'resume',
      label: 'Upload Resume/CV',
      required: false,
      id: 'attachment',
      selectedFiles: [],
      descId: 'id',
      supportedExtensions: ACCEPTED_FILE_TYPES,
      supportedFiles: [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ],
      acceptedFiles: 'upload .pdf, .doc, .docx files',
      type: 'file_upload',
      value: null,
      needConfirmation: true,
      disabled: false,
    },
  ]

  const MotivationAndGoals = [
    {
      name: 'why_interested',
      label: 'Why are you interested in this internship?',
      type: 'textarea',
      placeholder: 'Enter your Opinion',
      disabled: false,
      rows: 4,
      required: true,
    },
  ]

  const Declarations = [
    {
      name: 'information_accurate',
      label: 'I confirm that the information provided is accurate',
      type: 'custom_checkbox',
      required: true,
      className: 'orange-checkbox',
    },
    {
      name: 'internship_program',
      label:
        'I agree to abide by the rules and regulations of the internship program.',
      type: 'custom_checkbox',
      required: true,
      className: 'orange-checkbox',
    },
  ]

  return (
    <FormProvider {...methods}>
      <div>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col lg:flex-row gap-6"
        >
          <div className="w-full lg:w-3/4 space-y-10">
            {/* Personal Details Section */}
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-regular">Personal Details</h2>
                <p className="text-LightGray text-xs">
                  Please Fill Out All Required Fields
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormBuilder data={personalDetails} edit />
              </div>
            </div>

            {/* Educational Background Section */}
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-regular">Educational Background</h2>
                <p className="text-LightGray text-xs">
                  Please Fill Out All Required Fields
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormBuilder data={educationDetails} edit />
              </div>
            </div>

            {/* Internship Preferences Background Section */}
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-regular">Internship Preferences</h2>
                <p className="text-LightGray text-xs">
                  Please Fill Out All Required Fields
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormBuilder data={internshipPreferance} edit />
              </div>
            </div>

            {/* Experience and Skills Background Section */}
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-regular">Experience and Skills</h2>
                <p className="text-LightGray text-xs">
                  Please Fill Out All Required Fields
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormBuilder data={experienceSkills} edit />
              </div>
            </div>

            {/* Motivation & Goals Background Section */}
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-regular">Motivation & Goals</h2>
                <p className="text-LightGray text-xs">
                  Please Fill Out All Required Fields
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormBuilder data={MotivationAndGoals} />
              </div>
            </div>

            {/* Declarations Background Section */}
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-regular">Declarations</h2>
                <p className="text-LightGray text-xs">
                  Please Fill Out All Required Fields
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormBuilder data={Declarations} edit />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 mt-4 w-full">
              <Button
                type="submit"
                className="px-4 py-2 text-white rounded disabled:opacity-50 w-full !bg-primaryOrange"
                disabled={isCreating}
                label="Submit"
              />
            </div>
          </div>

          {/* Right Side - Image */}
          <div className="hidden lg:block w-full lg:w-1/4">
            <img
              src={intershipImage}
              alt="Internship Illustration"
              className="w-full h-auto object-cover rounded"
            />
          </div>
        </form>
      </div>
    </FormProvider>
  )
}
